import moment from 'moment-timezone';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { AlertTooltip } from '@payaca/components/iconTooltip/IconTooltip';
import ProgressIndicator from '@payaca/components/progressIndicator/ProgressIndicator';

import {
  useServicePlan,
  useServicePlanSubscription,
} from '@payaca/store/hooks/appState';
import * as servicePlansActions from '@payaca/store/servicePlans/servicePlansActions';

import { PublicHydratedServicePlanPeriod } from '@payaca/types/service-plans';

import UntitledIcon from '@payaca/untitled-icons';
import { ServicePlanPeriodStatusBadge } from '../servicePlanPeriodStatusBadge/ServicePlanPeriodStatusBadge';

/** Plan name for a period. Fetches the plan if it is not already in the store. */
export const ServicePeriodPlanNameCell = ({
  servicePlanPublicId,
}: {
  servicePlanPublicId: string;
}) => {
  const dispatch = useDispatch();
  const servicePlan = useServicePlan(servicePlanPublicId);

  useEffect(() => {
    if (!servicePlan) {
      dispatch(
        servicePlansActions.getServicePlan.request({
          publicId: servicePlanPublicId,
        })
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only refetch when the id changes
  }, [servicePlanPublicId]);

  return <>{servicePlan?.name || '-'}</>;
};

/** Commitment progress indicators, grouped by events and tasks. */
export const ServicePeriodCommitmentsCell = ({
  commitments,
}: {
  commitments: PublicHydratedServicePlanPeriod['commitments'];
}) => {
  const { events: eventsCommitments, tasks: tasksCommitments } = useMemo(() => {
    const { events, tasks } = commitments.reduce(
      (
        acc: {
          events: { className: string; publicId: string; sortOrder: number }[];
          tasks: { className: string; publicId: string; sortOrder: number }[];
        },
        commitment
      ) => {
        const details = {
          className: '',
          publicId: commitment.publicId,
          sortOrder: 2,
        };
        if (
          commitment.status === 'fulfilled' ||
          commitment.isFulfilledOverride
        ) {
          details.className = 'fulfilled';
          details.sortOrder = 0;
        } else if (commitment.status === 'arranged') {
          details.className = 'arranged';
          details.sortOrder = 1;
        }
        if (commitment.entityType === 'scheduledEvent') {
          acc.events.push(details);
        } else if (commitment.entityType === 'task') {
          acc.tasks.push(details);
        }
        return acc;
      },
      { events: [], tasks: [] }
    );
    return {
      tasks: tasks.slice().sort((a, b) => a.sortOrder - b.sortOrder),
      events: events.slice().sort((a, b) => a.sortOrder - b.sortOrder),
    };
  }, [commitments]);

  if (!eventsCommitments.length && !tasksCommitments.length) {
    return <>-</>;
  }

  return (
    <div className="flex items-center gap-3 [&_.arranged]:text-yellow-500 [&_.fulfilled]:text-green-500">
      {!!eventsCommitments.length && (
        <div className="flex items-center gap-1">
          <UntitledIcon name="calendar" className="size-4" />
          {eventsCommitments.map((commitmentEvent) => (
            <ProgressIndicator
              key={`commitment-event-indicator-${commitmentEvent.publicId}`}
              className={commitmentEvent.className}
            />
          ))}
        </div>
      )}
      {!!tasksCommitments.length && (
        <div className="flex items-center gap-1">
          <UntitledIcon className="size-4" name="list.3" />
          {tasksCommitments.map((commitmentTask) => (
            <ProgressIndicator
              key={`commitment-task-indicator-${commitmentTask.publicId}`}
              className={commitmentTask.className}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Status badge plus overdue / ended-subscription warnings. Fetches the
 * subscription if it is not already in the store so it can flag a cancellation.
 */
export const ServicePeriodStatusCell = ({
  servicePlanPeriod,
}: {
  servicePlanPeriod: PublicHydratedServicePlanPeriod;
}) => {
  const dispatch = useDispatch();
  const servicePlanSubscription = useServicePlanSubscription(
    servicePlanPeriod.servicePlanSubscriptionPublicId
  );

  useEffect(() => {
    if (!servicePlanSubscription) {
      dispatch(
        servicePlansActions.getServicePlanSubscription.request({
          publicId: servicePlanPeriod.servicePlanSubscriptionPublicId,
        })
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only refetch when the id changes
  }, [servicePlanPeriod.servicePlanSubscriptionPublicId]);

  const isOverdue = useMemo(
    () =>
      moment(servicePlanPeriod.endsAt).isBefore(moment.now()) &&
      servicePlanPeriod.status !== 'fulfilled',
    [servicePlanPeriod.endsAt, servicePlanPeriod.status]
  );

  const isCurrent = useMemo(
    () =>
      moment(servicePlanPeriod.startsAt).isBefore(moment.now()) &&
      moment(servicePlanPeriod.endsAt).isAfter(moment.now()),
    [servicePlanPeriod.startsAt, servicePlanPeriod.endsAt]
  );

  return (
    <div className="flex items-center gap-2">
      {isOverdue && (
        <AlertTooltip tooltipText={'Service plan period is overdue'} />
      )}
      {(isCurrent || isOverdue) &&
        servicePlanSubscription?.status === 'cancelled' && (
          <AlertTooltip tooltipText={'Service plan subscription has ended'} />
        )}
      <ServicePlanPeriodStatusBadge status={servicePlanPeriod.status} />
    </div>
  );
};
